/**
 * Returns true when the string
 *   • has http/https or no scheme (no other schemes allowed),
 *   • host labels contain only A-Z a-z 0-9 _ - and include at least one “.tld”
 *     whose TLD is ≥ 2 ASCII letters,
 *   • may include a path, *query string* and/or *fragment*,
 *   • every character after the first “/” (including inside ?… or #…) is either
 *       – an ASCII letter / digit / one of . _ ~ % ! $ & ' ( ) * + , ; = : @ - / ? #
 *       – or any non-ASCII code-point (covers emoji, CJK, etc.).
 *
 * Stays ES-5-compatible: uses no modern RegExp features, only `new URL`,
 * `encodeURI`, and simple char-code checks.
 */
export function isValidUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;

  const str = value.trim();
  if (!str) return false;

  /* ── 1. add dummy http:// if user omitted a scheme ─────────────────── */
  const hasScheme = str.includes("://");
  const working = hasScheme ? str : `http://${str}`;

  /* ── 2. parse safely (encodeURI guards against raw emoji) ──────────── */
  let url: URL;
  try {
    url = new URL(encodeURI(working));
  } catch {
    return false; // malformed
  }

  /* ── 3. scheme check ───────────────────────────────────────────────── */
  if (hasScheme) {
    const proto = url.protocol.toLowerCase();
    if (proto !== "http:" && proto !== "https:") return false;
  }

  /* ── 4. hostname check (labels A-Z a-z 0-9 _ - , ends in .TLD≥2) ───── */
  const host = url.hostname; // punycode-decoded
  const parts = host.split(".");
  if (parts.length < 2) return false; // need at least one dot
  const tld = parts.pop()!;
  if (tld.length < 2 || /[^A-Za-z]/.test(tld)) return false;
  for (const label of parts) {
    if (!label) return false; // no empty “..”
    if (/[^A-Za-z0-9_-]/.test(label)) return false;
  }

  /* ── 5. character-level check for path + search + hash ─────────────── */
  const remainder = decodeURIComponent(url.pathname + url.search + url.hash); // brings emoji back

  if (remainder && remainder[0] !== "/") return false;

  const asciiOK = "._~%!$&'()*+,;=:@-/?#="; // allowed extras
  for (const ch of remainder) {
    const code = ch.codePointAt(0)!;

    // Control chars or space ⇒ reject
    if (code <= 0x20 || code === 0x7f) return false;

    if (code < 0x80) {
      // ASCII range
      const isAlpha = (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
      const isDigit = code >= 48 && code <= 57;
      const isAllowed = isAlpha || isDigit || asciiOK.includes(ch);
      if (!isAllowed) return false;
    }
    // ≥ 0x80  ⇒ accept outright (emoji, CJK, etc.)
  }

  return true; // ✅ all checks passed
}
